import { useState } from 'react';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import {
  RiTestTubeLine,
  RiSettings3Line,
  RiShieldCheckLine,
  RiAlertLine,
} from '@remixicon/react';
import { useSandboxStore } from '@/stores/sandbox-store';
import { cn } from '@/lib/utils';

interface SandboxModeToggleProps {
  databaseId?: string;
  className?: string;
}

export function SandboxModeToggle({ databaseId, className }: SandboxModeToggleProps) {
  const [showSettings, setShowSettings] = useState(false);

  const {
    enabled,
    autoEnableForDestructive,
    databaseOverrides,
    setEnabled,
    setAutoEnableForDestructive,
    setDatabaseOverride,
    removeDatabaseOverride,
  } = useSandboxStore();

  const hasOverride = databaseId ? databaseOverrides[databaseId] !== undefined : false;
  const effectiveEnabled = databaseId && hasOverride ? databaseOverrides[databaseId] : enabled;

  const handleToggle = (checked: boolean) => {
    if (databaseId) {
      setDatabaseOverride(databaseId, checked);
    } else {
      setEnabled(checked);
    }
  };

  const handleRemoveOverride = () => {
    if (databaseId) {
      removeDatabaseOverride(databaseId);
    }
  };

  return (
    <div className={cn('flex items-center gap-2', className)}>
      {/* Main Toggle */}
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border bg-card">
        <RiTestTubeLine
          className={cn(
            'h-4 w-4 transition-colors',
            effectiveEnabled ? 'text-green-600 dark:text-green-500' : 'text-muted-foreground'
          )}
        />
        <Label
          htmlFor="sandbox-toggle"
          className={cn(
            'text-sm font-medium cursor-pointer transition-colors',
            effectiveEnabled ? 'text-foreground' : 'text-muted-foreground'
          )}
        >
          Sandbox Mode
        </Label>
        <Switch
          id="sandbox-toggle"
          checked={effectiveEnabled}
          onCheckedChange={handleToggle}
          className="data-[state=checked]:bg-green-600"
        />
        {effectiveEnabled && (
          <div className="flex items-center gap-1 text-xs text-green-600 dark:text-green-500">
            <RiShieldCheckLine className="h-3 w-3" />
            <span className="font-medium">Safe</span>
          </div>
        )}
      </div>

      {/* Settings Popover */}
      <Popover open={showSettings} onOpenChange={setShowSettings}>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8">
            <RiSettings3Line className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80" align="end">
          <div className="space-y-4">
            <div>
              <h4 className="font-semibold text-sm mb-1">Sandbox Mode Settings</h4>
              <p className="text-xs text-muted-foreground">
                Control when queries are tested safely before execution
              </p>
            </div>

            <Separator />

            {/* Global Toggle */}
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <Label htmlFor="global-sandbox" className="text-sm font-medium">
                  Enable Sandbox Mode
                </Label>
                <p className="text-xs text-muted-foreground mt-1">
                  All queries run in safe mode first
                </p>
              </div>
              <Switch
                id="global-sandbox"
                checked={enabled}
                onCheckedChange={setEnabled}
                className="data-[state=checked]:bg-green-600"
              />
            </div>

            {/* Smart Auto-Enable */}
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <Label htmlFor="auto-enable" className="text-sm font-medium flex items-center gap-1">
                  Smart Mode
                  <span className="text-xs text-primary font-normal">(Recommended)</span>
                </Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Auto-enable for UPDATE, DELETE, DROP, etc.
                </p>
              </div>
              <Switch
                id="auto-enable"
                checked={autoEnableForDestructive}
                onCheckedChange={setAutoEnableForDestructive}
                className="data-[state=checked]:bg-primary"
              />
            </div>

            {/* Database Override Indicator */}
            {hasOverride && databaseId && (
              <>
                <Separator />
                <div className="bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-900/50 p-3 rounded-lg space-y-2">
                  <div className="flex items-start gap-2">
                    <RiAlertLine className="h-4 w-4 text-yellow-600 dark:text-yellow-500 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-xs font-medium text-yellow-900 dark:text-yellow-100">
                        Database Override Active
                      </p>
                      <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-0.5">
                        This database has custom sandbox settings
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRemoveOverride}
                    className="w-full text-xs h-7 border-yellow-300 hover:bg-yellow-100 dark:border-yellow-800 dark:hover:bg-yellow-900/20"
                  >
                    Use Global Settings
                  </Button>
                </div>
              </>
            )}

            {/* Help Text */}
            <div className="bg-muted p-3 rounded-lg space-y-2">
              <div className="flex items-start gap-2">
                <RiShieldCheckLine className="h-4 w-4 text-green-600 dark:text-green-500 mt-0.5" />
                <div>
                  <p className="text-xs font-medium">How Sandbox Mode Works</p>
                  <ul className="text-xs text-muted-foreground mt-1 space-y-1">
                    <li>• Queries run in a test transaction first</li>
                    <li>• Changes are rolled back automatically</li>
                    <li>• See exactly what would happen</li>
                    <li>• Execute for real only when ready</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
